import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import "./InternalDebuggerJsonInput.scss";

// Simple localStorage hook replacement
const useLocalStorage = <T,>(key: string, defaultValue: T): [T, (value: T | ((prev: T) => T)) => void, boolean] => {
  const [value, setValue] = useState<T>(() => {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch {
      return defaultValue;
    }
  });
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setLoaded(true);
  }, []);

  const setStoredValue = useCallback(
    (newValue: T | ((prev: T) => T)) => {
      setValue(newValue);
      try {
        const finalValue = typeof newValue === "function" ? (newValue as (prev: T) => T)(value) : newValue;
        localStorage.setItem(key, JSON.stringify(finalValue));
      } catch {
        // Ignore errors
      }
    },
    [key, value],
  );

  return [value, setStoredValue, loaded];
};

// Simple compress/decompress replacement
const compress = (data: any) => data;
const decompress = (data: any) => data;

export interface DebuggerJsonInputProps {
  label: string;
  id: string;
  value?: string;
  onValueChange?: (value: string) => void;
  onValidValueChange?: (value: any) => void;
  onValidateValue?: (value: any) => boolean;
}

export interface RecentEntry {
  name: string;
  content: string;
}

export interface JsonRecentEntryProps {
  entry: RecentEntry;
  onSelect: (file: RecentEntry) => void;
  onRemove: (file: RecentEntry) => void;
}

const JsonRecentEntry = (props: JsonRecentEntryProps) => {
  const { entry, onSelect, onRemove } = props;
  return (
    <div className="JsonRecentEntry">
      <span onClick={() => onSelect(entry)}>{entry.name}</span>
      <button
        onClick={(e) => {
          onRemove(entry);
          e.preventDefault();
        }}
      >
        x
      </button>
    </div>
  );
};

const DebuggerJsonInput = (props: DebuggerJsonInputProps) => {
  const { label, id, value, onValueChange, onValidValueChange, onValidateValue } = props;
  const [recents, setRecents, recentsLoaded] = useLocalStorage<string[]>(`json_input_${id}_recent_ids`, []);

  const [valid, setValid] = useState(false);

  const saveEntry = useCallback(
    (entry: RecentEntry) => {
      setRecents((recents) => {
        const fileKeys = [...recents];
        fileKeys.unshift(entry.name);
        const files = new Set(fileKeys);
        localStorage.setItem(`json_input_${id}[${entry.name}]`, JSON.stringify(compress(JSON.parse(entry.content))));
        return Array.from(files) as string[];
      });
    },
    [id, setRecents],
  );

  const handleValueChange = useCallback(
    (value: string, updateTextarea: boolean) => {
      if (updateTextarea) {
        textAreaRef.current!.value = value;
      }
      if (onValueChange) {
        onValueChange(value);
      }

      let validValue: any = undefined;

      try {
        validValue = JSON.parse(value);

        if (onValidateValue) {
          if (!onValidateValue(validValue)) {
            validValue = undefined;
          }
        }
      } catch {
        validValue = undefined;
      }

      if (validValue) {
        if (onValidValueChange) {
          onValidValueChange(validValue);
        }
        setValid(true);
      } else {
        setValid(false);
      }
    },
    [onValueChange, onValidValueChange, onValidateValue],
  );

  const handleFileUpload = useCallback(
    async (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      let value = e.target.value;
      let updateTextarea = false;

      if ((e.target as any).files) {
        const file: File = (e.target as any).files[0];
        value = await file.text();
        updateTextarea = true;

        saveEntry({
          name: file.name,
          content: value,
        });
      }

      handleValueChange(value, updateTextarea);
    },
    [saveEntry, handleValueChange],
  );

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);

  const handlePickFile = useCallback(() => {
    fileInputRef.current!.click();
  }, []);

  const handleRemoveFile = useCallback(
    (file: RecentEntry) => {
      const index = recents!.indexOf(file.name);
      if (index >= 0) {
        recents?.splice(index, 1);
      }
      setRecents([...recents!]);
    },
    [recents, setRecents],
  );

  const handleSaveSubmit = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      const target = event.target as HTMLFormElement;
      const name = new FormData(target).get("entryName")! as string;
      saveEntry({
        name: name,
        content: textAreaRef.current!.value,
      });
      target.reset();
    },
    [saveEntry],
  );

  const getRecentContent = useCallback(
    (key: string): any => {
      const value = localStorage.getItem(`json_input_${id}[${key}]`);
      return value ? JSON.stringify(decompress(JSON.parse(value)), null, 2) : null;
    },
    [id],
  );

  useEffect(() => {
    if (value === undefined && recentsLoaded) {
      const firstKey = recents![0];
      if (firstKey) {
        handleValueChange(getRecentContent(firstKey), true);
      }
    }
  }, [recentsLoaded, recents, handleValueChange, getRecentContent]);

  return (
    <div className="JsonInput">
      <div className="JsonInput__label">
        <label htmlFor={id}>
          {!valid ? "❌" : "✔️"}
          {label}
        </label>
        <div>
          <button onClick={handlePickFile}>Choose file</button>
          <input
            accept=".json,application/JSON"
            onChange={handleFileUpload}
            ref={(element) => {
              fileInputRef.current = element;
            }}
            type={"file"}
          />
        </div>
      </div>
      <textarea
        ref={(input) => {
          textAreaRef.current = input;
        }}
        value={value}
        onChange={handleFileUpload}
        draggable={false}
        id={id}
      />
      <form
        onSubmit={handleSaveSubmit}
        className="JsonInput__save"
      >
        <input
          name={"entryName"}
          type={"text"}
        />{" "}
        <button type={"submit"}>Save</button>
      </form>

      <div className="JsonInput__recent-entries">
        {(recents || []).map((entryKey, index) => (
          <JsonRecentEntry
            key={index}
            entry={{ name: entryKey, content: getRecentContent(entryKey) }}
            onRemove={handleRemoveFile}
            onSelect={(entry) => {
              saveEntry(entry);
              handleValueChange(entry.content, true);
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default DebuggerJsonInput;
